import {
  AI_IMAGE_ATTACHMENT_DROPDOWN_OPTIONS,
  AI_IMAGE_MAX_REFERENCE_IMAGES,
  AI_IMAGE_MODEL_CARDS,
  AiImageAspectRatioOptions,
  AiImageFilterOptions,
  AiImageModelOption,
  AiImageStylesOptions,
  DEFAULT_SELECTED_AI_IMAGE_MODEL,
  AI_IMAGE_MAX_PROMPT_LENGTH,
  MediaTypePrompt,
  SEEDREAM_4_5_MAX_REFERENCE_IMAGES,
  CURRENT_PAGE_SNAPSHOT_FILENAME,
  CURRENT_PAGE_SNAPSHOT_MAX_DIMENSION,
  CURRENT_PAGE_SNAPSHOT_REFERENCE_SLOTS,
} from './ai-image-panel.types';
import {
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_NOT_AN_IMAGE_REQUEST_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE,
} from '@Libraries/pmw-stock-media-library';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
  type ContextualMenuItemWithEmoji,
  type ContextualMenuItemWithIcon,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import type {AiPromptBox as AiPromptBoxType} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {SkeletonAnimationType} from '@Components/base-grid/components/grid-skeleton-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {isUserPremium} from '@Libraries/user.library';
import type {AiImageReferenceImage} from '@Libraries/pmw-stock-media-library';
import {AiImageReferenceImageType} from '@Libraries/pmw-stock-media-library';
import {uploadAiReferenceAttachmentFile} from '@Components/ai-prompt-box/libraries/ai-prompt-box-reference-image-upload.library';
import {dataUrlToBlob} from '@Utils/file.util';
import {assertNever} from '@Utils/assert-never.util';
import type {AiImageGenerationState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';

export const AI_IMAGE_GENERATION_SKELETON_ID_PREFIX = 'ai-image-generation-skeleton-';

const AI_IMAGE_PROMPT_REJECTION_CODES: string[] = [
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  GENERATE_IMAGE_NOT_AN_IMAGE_REQUEST_RESPONSE_CODE,
];

export const getSkeletonIdForAiImageGeneration = (generation: AiImageGenerationState): string => {
  return `${AI_IMAGE_GENERATION_SKELETON_ID_PREFIX}${generation.id}`;
};

export const getSkeletonGridItemForAiImageGeneration = (generation: AiImageGenerationState): GridSkeletonItemStorage => {
  return {
    ...getAiSkeletonItem(getSkeletonIdForAiImageGeneration(generation)),
    ...getDimensionsForAiImageSkeletonItem(generation.aspectRatio),
    animationType: SkeletonAnimationType.AI,
  };
};

/**
 * Inverts the `model.toString()` encoding the contextual menu stores its selection in. The menu
 * holds every value as a string and drops the group's key entirely when nothing is active, so an id
 * read back out of it is only trustworthy once it has matched a real enum member — anything else
 * would reach `getCreditCostForModel` and throw out of `assertNever`.
 */
export const getAiImageModelOptionFromMenuValue = (menuValue: string | undefined): AiImageModelOption => {
  const knownModel = Object.values(AiImageModelOption).find(
    (modelOption): modelOption is AiImageModelOption => typeof modelOption === 'number' && modelOption.toString() === menuValue
  );
  return knownModel ?? DEFAULT_SELECTED_AI_IMAGE_MODEL;
};

export const getDimensionsForAiImageSkeletonItem = (aspectRatio: AiImageAspectRatioOptions): Partial<GridSkeletonItemStorage> => {
  switch (aspectRatio) {
    case AiImageAspectRatioOptions.Portrait:
      return {width: 40};
    case AiImageAspectRatioOptions.LandScape:
      return {width: 122};
    case AiImageAspectRatioOptions.Square:
    default:
      return {width: 70};
  }
};

export const getAiSkeletonItem = (id: string): GridSkeletonItemStorage => {
  return {
    id: id,
    type: GRID_ITEM_TYPE.SKELETON,
    width: 180,
    height: 70,
    useFixedDimensions: false,
  };
};

/**
 * The response code the generate endpoint rejected the prompt with, or undefined when the failure
 * was not a prompt rejection. Anything undefined is a transient failure the user can retry as is.
 *
 * A rejected prompt reaches the client as the response code in the thrown error's message
 * (core.js `getErrorAjaxResponse` builds the Error from `response.message`, and the generate
 * endpoint answers with the exception's JSON error code).
 */
export const getAiImageGenerationErrorCode = (errorMessage: string): string | undefined => {
  return AI_IMAGE_PROMPT_REJECTION_CODES.find((code) => errorMessage.includes(code));
};

export const getAiImageGenerationErrorMessage = (errorCode: string | undefined): string => {
  switch (errorCode) {
    case GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE:
      return window.i18next.t('pmwjs_search_prompt_violation_message');
    case GENERATE_IMAGE_VAGUE_PROMPT_RESPONSE_CODE:
      return window.i18next.t('pmwjs_vague_prompt_error');
    case GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE:
      return window.i18next.t('pmwjs_search_prompt_unsupported_language_message');
    case GENERATE_IMAGE_NOT_AN_IMAGE_REQUEST_RESPONSE_CODE:
      return window.i18next.t('pmwjs_ai_image_not_an_image_request');
    default:
      return window.i18next.t('pmwjs_unexpected_error_occurred');
  }
};

export const getCreditCostForModel = (value: AiImageModelOption): number => {
  switch (value) {
    case AiImageModelOption.StabilityAICore:
    case AiImageModelOption.StabilityAISDXL:
    case AiImageModelOption.DeepInfraFluxSchnell:
    case AiImageModelOption.Auto:
    case AiImageModelOption.Gemini2_5:
    case AiImageModelOption.ModelArkSeedream5_0Lite:
    case AiImageModelOption.DeepInfraSeedream4_5:
    case AiImageModelOption.DeepInfraFlux2Pro:
    case AiImageModelOption.GeminiNanoBanana2Lite:
      return 1;
    case AiImageModelOption.StabilityAIUltra:
    case AiImageModelOption.OpenAIGptImage2:
    case AiImageModelOption.OpenAIGptImage2_5:
    case AiImageModelOption.GeminiNanoBanana2:
      return 2;
    default:
      return assertNever(value);
  }
};

export const getMaxReferenceImagesForModel = (model: AiImageModelOption): number => {
  return model === AiImageModelOption.DeepInfraSeedream4_5 ? SEEDREAM_4_5_MAX_REFERENCE_IMAGES : AI_IMAGE_MAX_REFERENCE_IMAGES;
};

export const getMaxUserReferenceImagesForModel = (model: AiImageModelOption, mediaTypePrompt: MediaTypePrompt): number => {
  return Math.max(0, getMaxReferenceImagesForModel(model) - getReservedReferenceImageCount(mediaTypePrompt));
};

const getReservedReferenceImageCount = (mediaTypePrompt: MediaTypePrompt): number => {
  return mediaTypePrompt === MediaTypePrompt.TEXT_ART ? CURRENT_PAGE_SNAPSHOT_REFERENCE_SLOTS : 0;
};

export const getPromptBoxOptions = (
  mediaTypePrompt: MediaTypePrompt,
  model: AiImageModelOption,
  style: AiImageStylesOptions | null,
  aspectRatio: AiImageAspectRatioOptions
): AiPromptBoxType => {
  return {
    placeholder: getPlaceHolderText(mediaTypePrompt),
    showInitialAnimation: false,
    contextualMenu: getContextualMenuGroups(model, style, aspectRatio, mediaTypePrompt),
    attachments: {
      items: [],
      maxAttachments: getMaxUserReferenceImagesForModel(model, mediaTypePrompt),
      dropdown: AI_IMAGE_ATTACHMENT_DROPDOWN_OPTIONS,
      pasteAsReferenceImages: true,
    },
    size: AiPromptBoxSize.SMALL,
    creditsPill: {
      visible: false,
      actionCost: 0,
      remainingCredits: 1,
      showPremiumIcon: !isUserPremium(),
    },
    input: {
      hideScrollbar: true,
      maxPromptLength: AI_IMAGE_MAX_PROMPT_LENGTH,
    },
    promptBoxState: PromptBoxState.IDLE,
    prompt: '',
  };
};

export const getDefaultStyleForMediaType = (mediaTypePrompt: MediaTypePrompt): AiImageStylesOptions | null => {
  if (mediaTypePrompt === MediaTypePrompt.STICKER) {
    return AiImageStylesOptions.DigitalArt;
  }
  if (mediaTypePrompt === MediaTypePrompt.TEXT_ART) {
    return null;
  }
  return AiImageStylesOptions.Photographic;
};

export const getSelectedStyle = (selectedStyleValues: string[] | undefined): AiImageStylesOptions | null => {
  const selectedValue = selectedStyleValues?.[0];
  return Object.values(AiImageStylesOptions).find((style) => style === selectedValue) ?? null;
};

export const getPlaceHolderText = (mediaTypePrompt: MediaTypePrompt): string => {
  if (mediaTypePrompt === MediaTypePrompt.LOGO) {
    return window.i18next.t('pmwjs_describe_logo_for_ai_image_generation');
  }
  if (mediaTypePrompt === MediaTypePrompt.STICKER) {
    return window.i18next.t('pmwjs_describe_sticker_for_ai_image_generation');
  }
  if (mediaTypePrompt === MediaTypePrompt.TEXT_ART) {
    return window.i18next.t('pmwjs_describe_text_art_for_ai_image_generation');
  }
  return window.i18next.t('pmwjs_describe_image_for_ai_image_generation');
};

export const getContextualMenuGroups = (
  model: AiImageModelOption,
  style: AiImageStylesOptions | null,
  aspectRatio: AiImageAspectRatioOptions,
  mediaTypePrompt: MediaTypePrompt,
  attachmentCount = 0
): ContextualMenuGroup[] => {
  return [getAspectRatioMenuGroup(aspectRatio, mediaTypePrompt), getAiModelMenuGroup(model, attachmentCount + getReservedReferenceImageCount(mediaTypePrompt), mediaTypePrompt), getStyleMenuGroup(style)];
};

const getStyleMenuGroup = (style: AiImageStylesOptions | null): ContextualMenuGroup => {
  return {
    label: window.i18next.t('pmwjs_style'),
    key: AiImageFilterOptions.Styles,
    type: ContextualMenuGroupType.PILLS,
    items: [
      {
        label: window.i18next.t('pmwjs_photographic'),
        value: AiImageStylesOptions.Photographic,
        active: style === AiImageStylesOptions.Photographic,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '📷',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_digital_art'),
        value: AiImageStylesOptions.DigitalArt,
        active: style === AiImageStylesOptions.DigitalArt,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '💻',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_three_d_model'),
        value: AiImageStylesOptions.Model3d,
        active: style === AiImageStylesOptions.Model3d,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '📦',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_cinematic'),
        value: AiImageStylesOptions.Cinematic,
        active: style === AiImageStylesOptions.Cinematic,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '🎬',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_futuristic'),
        value: AiImageStylesOptions.NeonPunk,
        active: style === AiImageStylesOptions.NeonPunk,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '🤖',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_line_art'),
        value: AiImageStylesOptions.LineArt,
        active: style === AiImageStylesOptions.LineArt,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '✏️',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_fantasy'),
        value: AiImageStylesOptions.FantasyArt,
        active: style === AiImageStylesOptions.FantasyArt,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '🐉',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_anime'),
        value: AiImageStylesOptions.Anime,
        active: style === AiImageStylesOptions.Anime,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '👩‍🎤',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_pixel_art'),
        value: AiImageStylesOptions.PixelArt,
        active: style === AiImageStylesOptions.PixelArt,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '👾',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_vintage'),
        value: AiImageStylesOptions.AnalogFilm,
        active: style === AiImageStylesOptions.AnalogFilm,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '📻',
      } as ContextualMenuItemWithEmoji,
      {
        label: window.i18next.t('pmwjs_comic'),
        value: AiImageStylesOptions.ComicBook,
        active: style === AiImageStylesOptions.ComicBook,
        graphicType: ContextualMenuItemGraphicType.EMOJI,
        emoji: '🦸🏻‍♂️',
      } as ContextualMenuItemWithEmoji,
    ],
  };
};

const getAspectRatioMenuGroup = (aspectRatio: AiImageAspectRatioOptions, mediaTypePrompt: MediaTypePrompt): ContextualMenuGroup => {
  return {
    label: window.i18next.t('pmwjs_aspect_ratio'),
    key: AiImageFilterOptions.AspectRatio,
    type: ContextualMenuGroupType.PILLS,
    items: [
      {
        label: window.i18next.t('pmwjs_square'),
        value: AiImageAspectRatioOptions.Square,
        active: aspectRatio === AiImageAspectRatioOptions.Square,
        graphicType: ContextualMenuItemGraphicType.ICON,
        icon: 'icon-square',
        disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
      } as ContextualMenuItemWithIcon,
      {
        label: window.i18next.t('pmwjs_landscape'),
        value: AiImageAspectRatioOptions.LandScape,
        active: aspectRatio === AiImageAspectRatioOptions.LandScape,
        graphicType: ContextualMenuItemGraphicType.ICON,
        icon: 'icon-landscape',
        disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
      } as ContextualMenuItemWithIcon,
      {
        label: window.i18next.t('pmwjs_portrait'),
        value: AiImageAspectRatioOptions.Portrait,
        active: aspectRatio === AiImageAspectRatioOptions.Portrait,
        graphicType: ContextualMenuItemGraphicType.ICON,
        icon: 'icon-portrait',
        disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
      } as ContextualMenuItemWithIcon,
    ],
  };
};

const getAiModelMenuGroup = (model: AiImageModelOption, attachmentCount: number, mediaTypePrompt: MediaTypePrompt): ContextualMenuGroup => {
  return {
    label: window.i18next.t('pmwjs_ai_model'),
    key: AiImageFilterOptions.AiModel,
    type: ContextualMenuGroupType.CARDS,
    items: AI_IMAGE_MODEL_CARDS.map((card) => ({
      label: window.i18next.t(card.labelKey),
      value: card.model.toString(),
      active: model === card.model,
      description: window.i18next.t(card.descriptionKey),
      tooltip: getModelCardTooltip(card.model, attachmentCount, mediaTypePrompt),
      topRightIcon: 'icon-flash',
      credits: getCreditCostForModel(card.model),
    })),
  };
};

const getModelCardTooltip = (model: AiImageModelOption, attachmentCount: number, mediaTypePrompt: MediaTypePrompt): string | undefined => {
  if (attachmentCount <= getMaxReferenceImagesForModel(model)) {
    return undefined;
  }
  if (getMaxUserReferenceImagesForModel(model, mediaTypePrompt) === 0) {
    return window.i18next.t('pmwjs_model_supports_no_attachments_for_text_art_rest_will_be_removed');
  }
  return window.i18next.t('pmwjs_model_supports_one_attachment_only_rest_will_be_removed');
};

export const captureCurrentPageAsReferenceImage = async (): Promise<AiImageReferenceImage | null> => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return null;
  }

  const dataUrl = await currentPage.toDataURL({maxDimension: CURRENT_PAGE_SNAPSHOT_MAX_DIMENSION});
  const snapshotFile = new File([dataUrlToBlob(dataUrl)], CURRENT_PAGE_SNAPSHOT_FILENAME, {type: 'image/png'});
  const upload = await uploadAiReferenceAttachmentFile(snapshotFile);

  return {hashedFilename: upload.hashedFilename, type: AiImageReferenceImageType.AI_ATTACHMENT};
};
